import React, { useState, useRef, useEffect } from 'react';
import { Link as LinkIcon, CheckCircle, Calendar, Lock, Mail, Share2, Clock } from 'lucide-react';
import axios from '@/lib/http';
import { useAuth } from '../contexts/TestAuthContext';
import { useActivityLog } from '../hooks/useActivityLog';
import { useFocusTrap } from '../hooks/useFocusTrap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface ShareReportModalProps {
  reportId: string;
  reportName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface ShareLink {
  share_id: string;
  share_token: string;
  share_url: string;
  expires_at: string;
  watermark_text: string;
  view_count?: number;
  is_expired?: boolean;
}

export default function ShareReportModal({ reportId, reportName, isOpen, onClose }: ShareReportModalProps) {
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [existingShares, setExistingShares] = useState<ShareLink[]>([]);
  const [showExisting, setShowExisting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  
  // Options
  const [expiresDays, setExpiresDays] = useState(30);
  const [watermarkText, setWatermarkText] = useState('CONFIDENTIAL - READ-ONLY');

  const loadExistingShares = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/public-shares`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExistingShares(response.data);
      setShowExisting(true);
    } catch (err: any) {
      console.error('Failed to load shares:', err);
    }
  };

  const createShareLink = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/public-share`,
        null,
        {
          params: {
            expires_days: expiresDays,
            watermark_text: watermarkText
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setShareLink(response.data);
      
      // Log share activity
      logActivity({
        reportId,
        actionType: 'share',
        actionDetails: `Created share link for ${reportName}`,
        section: 'sharing',
        metadata: { expires_days: expiresDays, watermark: watermarkText }
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (shareLink?.share_url) {
      await navigator.clipboard.writeText(shareLink.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const revokeShare = async (shareId: string) => {
    try {
      const token = await getToken();
      await axios.delete(
        `${BACKEND_URL}/api/v1/collaboration/public-shares/${shareId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExistingShares(prev => prev.filter(s => s.share_id !== shareId));
    } catch (err: any) {
      setError('Failed to revoke share');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Share Report" className="rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {/* Header */}
        <div className="p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                <LinkIcon size={20} style={{ color: 'var(--color-needs-human-text)' }} />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Share Report</h2>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{reportName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ color: 'var(--color-text-dim)' }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Error */}
          {error && (
            <div className="mb-4 p-3 border rounded-lg text-sm" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
              {error}
            </div>
          )}

          {/* Share Link Created */}
          {shareLink ? (
            <div className="space-y-4">
              <div className="border rounded-lg p-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
                <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
                  <CheckCircle size={18} /> Share Link Created!
                </h3>
                <p className="text-sm mb-3" style={{ color: 'var(--color-verified)' }}>
                  Anyone with this link can view the report (read-only).
                </p>

                {/* Link Display */}
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    readOnly
                    value={shareLink.share_url}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
                    style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-verified)', color: 'var(--color-text-primary)' }}
                  />
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                    style={
                      copied
                        ? { backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }
                        : { backgroundColor: 'transparent', color: 'var(--color-verified)' }
                    }
                  >
                    {copied ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>

                <div className="text-xs space-y-1" style={{ color: 'var(--color-verified)' }}>
                  <p className="flex items-center gap-1"><Calendar size={12} /> Expires: {new Date(shareLink.expires_at).toLocaleDateString()}</p>
                  <p className="flex items-center gap-1"><Lock size={12} /> Watermark: {shareLink.watermark_text}</p>
                </div>

                {/* Email Share Options */}
                <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-verified)' }}>
                  <p className="text-sm font-medium mb-3 flex items-center gap-1" style={{ color: 'var(--color-verified)' }}><Mail size={14} /> Share via Email</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const subject = encodeURIComponent(`Business Interruption Claim Report - ${reportName}`);
                        const body = encodeURIComponent(
                          `I'm sharing a Business Interruption claim report with you for review.\n\n` +
                          `Report: ${reportName}\n` +
                          `View Report: ${shareLink.share_url}\n\n` +
                          `This is a read-only link that expires on ${new Date(shareLink.expires_at).toLocaleDateString()}.\n\n` +
                          `You can view the full report and download the PDF without needing an account.\n\n` +
                          `---\nSent via DisruptionGenius`
                        );
                        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                      }}
                      className="flex-1 px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                      style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Open Email Client
                    </button>
                    <button
                      onClick={() => {
                        const emailText = 
                          `Business Interruption Claim Report - ${reportName}\n\n` +
                          `View Report: ${shareLink.share_url}\n\n` +
                          `This read-only link expires on ${new Date(shareLink.expires_at).toLocaleDateString()}.`;
                        navigator.clipboard.writeText(emailText);
                        setStatusMessage('Email text copied! Paste into your email.');
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium"
                      style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                    >
                      Copy Email Text
                    </button>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  setShareLink(null);
                  onClose();
                }}
                className="w-full px-4 py-2 rounded-lg"
                style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Create New Share */}
              <div className="space-y-4">
                <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                  <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}><Share2 size={16} /> Generate Share Link</h3>
                  <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                    Create a read-only link to share with opposing counsel, adjusters, or other parties.
                    They won't need an account to view.
                  </p>
                </div>

                {/* Options */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Expires In
                    </label>
                    <select
                      value={expiresDays}
                      onChange={(e) => setExpiresDays(Number(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                    >
                      <option value={7}>7 days</option>
                      <option value={30}>30 days</option>
                      <option value={90}>90 days</option>
                      <option value={365}>1 year</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Watermark Text
                    </label>
                    <input
                      type="text"
                      value={watermarkText}
                      onChange={(e) => setWatermarkText(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                      placeholder="CONFIDENTIAL - READ-ONLY"
                    />
                  </div>
                </div>

                <button
                  onClick={createShareLink}
                  disabled={loading}
                  className="w-full px-4 py-3 disabled:opacity-50 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Creating...
                    </>
                  ) : (
                    <><LinkIcon size={16} className="inline mr-1" /> Generate Share Link</>
                  )}
                </button>
              </div>

              {/* Existing Shares */}
              <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
                <button
                  onClick={loadExistingShares}
                  className="text-sm flex items-center gap-1"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {showExisting ? '▼' : '▶'} View Existing Shares ({existingShares.length || '...'})
                </button>

                {showExisting && existingShares.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {existingShares.map((share) => (
                      <div
                        key={share.share_id}
                        className="p-3 rounded-lg border"
                        style={
                          share.is_expired
                            ? { backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border)' }
                            : { backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }
                        }
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <p className="font-medium flex items-center gap-1" style={{ color: share.is_expired ? 'var(--color-text-dim)' : 'var(--color-text-secondary)' }}>
                              {share.is_expired ? <><Clock size={12} /> Expired</> : <><LinkIcon size={12} /> Active</>}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                              Views: {share.view_count || 0} •
                              Expires: {new Date(share.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                          {!share.is_expired && (
                            <button
                              onClick={() => revokeShare(share.share_id)}
                              className="text-xs"
                              style={{ color: 'var(--color-needs-human-text)' }}
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
